/**
 * AuthProvider — session and authentication state provider.
 *
 * Wraps the application in an Auth context that exposes
 * sign-in, register, sign-out, and password reset via
 * the AuthService interface. Provider-agnostic: swap the
 * service implementation without touching UI or hooks.
 *
 * Now connected to Supabase Auth via supabaseAuthService.
 * Listens to onAuthStateChange to keep session in sync
 * across tabs and refreshes.
 *
 * PASSWORD_RECOVERY handling:
 * When a user clicks a Supabase recovery link, the auth
 * library fires INITIAL_SESSION (with the recovery session)
 * followed by PASSWORD_RECOVERY. We set a `passwordRecovery`
 * flag so GuestGuard does not redirect and useCreatorProfile
 * does not treat the session as a normal login. The recovery
 * session is kept active so updateUser({ password }) can
 * succeed on the ResetPasswordPage.
 */

import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { AuthContextValue } from './auth-context';
import type { User, UserCredentials, UserRegistration, PasswordResetRequest, AuthState } from '@/types/auth';
import type { AuthError } from '@/types/auth/result';
import { supabaseAuthService } from '@/services/auth';
import { getSupabase } from '@/lib/supabase';
import { analyticsService, globalErrorService, emailService } from '@/lib';

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function isRecoveryUrl(): boolean {
  if (typeof window === 'undefined') return false;
  const hash = window.location.hash;
  return hash.includes('type=recovery');
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<User | null>(null);
  const [authState, setAuthState] = useState<AuthState>('loading');
  const [error, setError] = useState<AuthError | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordRecovery, setPasswordRecovery] = useState(false);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Restore session on mount and subscribe to auth state changes
  useEffect(() => {
    let mounted = true;

    const recoveryFromUrl = isRecoveryUrl();

    (async () => {
      try {
        if (recoveryFromUrl) {
          // A recovery link was clicked. Supabase will fire INITIAL_SESSION
          // then PASSWORD_RECOVERY. Skip the normal getCurrentUser() check
          // so we don't briefly set 'authenticated' and trigger redirects.
          // The onAuthStateChange handler will set the correct state.
          if (mounted) setAuthState('unauthenticated');
          return;
        }

        const currentUser = await supabaseAuthService.getCurrentUser();
        if (mounted) {
          if (currentUser) {
            setUser(currentUser);
            setAuthState('authenticated');
          } else {
            setAuthState('unauthenticated');
          }
        }
      } catch {
        if (mounted) setAuthState('unauthenticated');
      }
    })();

    try {
      const supabase = getSupabase();
      const { data: subscription } = supabase.auth.onAuthStateChange((event, session) => {
        (async () => {
          if (!mounted) return;
          try {
            if (event === 'PASSWORD_RECOVERY') {
              // Keep the recovery session active (Supabase needs it for
              // updateUser), but mark it so guards/hooks treat this as a
              // recovery flow, not a normal login.
              setPasswordRecovery(true);
              if (session?.user) {
                const currentUser = await supabaseAuthService.getCurrentUser();
                if (mounted) {
                  setUser(currentUser);
                  setAuthState('authenticated');
                }
              } else if (mounted) {
                setUser(null);
                setAuthState('unauthenticated');
              }
              return;
            }

            if (session?.user) {
              const currentUser = await supabaseAuthService.getCurrentUser();
              if (mounted) {
                setUser(currentUser);
                setAuthState('authenticated');
                // During a recovery URL flow, INITIAL_SESSION fires before
                // PASSWORD_RECOVERY. Don't clear the recovery flag until the
                // PASSWORD_RECOVERY event has been processed. For normal
                // sessions (no recovery hash), clear it as usual.
                if (!isRecoveryUrl()) {
                  setPasswordRecovery(false);
                }
              }
            } else {
              if (mounted) {
                if (event === 'SIGNED_OUT') {
                  analyticsService.track('auth_session_expired');
                }
                setUser(null);
                setAuthState('unauthenticated');
                setPasswordRecovery(false);
              }
            }
          } catch {
            if (mounted) setAuthState('unauthenticated');
          }
        })();
      });

      return () => {
        mounted = false;
        subscription?.subscription.unsubscribe();
      };
    } catch {
      if (mounted) setAuthState('unauthenticated');
    }
  }, []);

  const signIn = useCallback(async (credentials: UserCredentials): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await supabaseAuthService.signIn(credentials);
      if (result.success && result.user) {
        setUser(result.user);
        setAuthState('authenticated');
        setPasswordRecovery(false);
        analyticsService.track('auth_login', { userId: result.user.id });
        analyticsService.identify(result.user.id, { email: result.user.email });
        return true;
      }
      setError(result.error ?? { code: 'unknown_error', message: 'Sign in failed.' });
      analyticsService.track('auth_login_failed', { reason: result.error?.code ?? 'unknown' });
      return false;
    } catch (err) {
      setError({ code: 'network_error', message: 'An unexpected error occurred.' });
      globalErrorService.capture(err, 'auth', { action: 'signIn' });
      analyticsService.track('auth_login_failed', { reason: 'network_error' });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const register = useCallback(async (data: UserRegistration): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await supabaseAuthService.register(data);
      if (result.success && result.user) {
        setUser(result.user);
        setAuthState('authenticated');
        setPasswordRecovery(false);
        analyticsService.track('auth_register', { userId: result.user.id });
        analyticsService.identify(result.user.id, { email: result.user.email });
        void emailService.send(
          result.user.email,
          'welcome',
          { name: result.user.fullName, verifyUrl: `${window.location.origin}/verify-email` },
          'high',
          { recipientUserId: result.user.id },
        ).catch((err) => {
          globalErrorService.capture(err, 'service', { template: 'welcome', recipient: result.user?.email });
        });
        return true;
      }
      setError(result.error ?? { code: 'unknown_error', message: 'Registration failed.' });
      return false;
    } catch (err) {
      setError({ code: 'network_error', message: 'An unexpected error occurred.' });
      globalErrorService.capture(err, 'auth', { action: 'register' });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const signOut = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    const previousUserId = user?.id;
    try {
      await supabaseAuthService.signOut();
    } finally {
      setUser(null);
      setAuthState('unauthenticated');
      setPasswordRecovery(false);
      setError(null);
      setIsLoading(false);
      analyticsService.track('auth_logout', { userId: previousUserId });
      analyticsService.reset();
    }
  }, [user?.id]);

  const requestPasswordReset = useCallback(
    async (request: PasswordResetRequest): Promise<boolean> => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await supabaseAuthService.requestPasswordReset(request);
        if (result.success) {
          analyticsService.track('auth_password_reset', { email: request.email });
          // The reset email is sent by the authentication provider, which owns the
          // single-use reset link. No additional mail is dispatched from the browser:
          // an unauthenticated visitor must not be able to make the platform deliver
          // mail to an address of their choosing.
          return true;
        }
        setError(result.error ?? { code: 'unknown_error', message: 'Request failed.' });
        return false;
      } catch (err) {
        setError({ code: 'network_error', message: 'An unexpected error occurred.' });
        globalErrorService.capture(err, 'auth', { action: 'passwordReset' });
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const resetPassword = useCallback(
    async (newPassword: string): Promise<boolean> => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await supabaseAuthService.resetPassword('', newPassword);
        if (result.success) {
          analyticsService.track('auth_password_reset', { action: 'complete' });
          setPasswordRecovery(false);
          return true;
        }
        setError(result.error ?? { code: 'unknown_error', message: 'Password update failed.' });
        return false;
      } catch (err) {
        setError({ code: 'network_error', message: 'An unexpected error occurred.' });
        globalErrorService.capture(err, 'auth', { action: 'resetPassword' });
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      state: authState,
      user,
      error,
      isLoading,
      passwordRecovery,
      signIn,
      register,
      signOut,
      requestPasswordReset,
      resetPassword,
      clearError,
    }),
    [authState, user, error, isLoading, passwordRecovery, signIn, register, signOut, requestPasswordReset, resetPassword, clearError],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
